import { getAccessToken } from "@/integrations/auth/client";

/**
 * Authenticated fetch wrapper for custom API routes (/api/...).
 *
 * Automatically injects the JWT Bearer token from Better Auth.
 * For JSON request bodies, Content-Type is set automatically.
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return fetch(url, { ...options, headers });
}

/**
 * Convenience wrapper that calls apiFetch, checks res.ok, and parses JSON.
 * Throws on non-2xx responses.
 */
export async function apiJson<T>(
  url: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await apiFetch(url, options);
  if (!res.ok) {
    const contentType = res.headers.get("Content-Type") ?? "";
    if (contentType.includes("application/json")) {
      const body = (await res.json().catch(() => null)) as {
        error?: string;
        awsError?: string;
        requestId?: string;
      } | null;
      if (body?.error) {
        const requestId = body.requestId
          ? ` (AWS request ${body.requestId})`
          : "";
        throw new Error(`${body.error}${requestId}`);
      }
    }

    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}
